import React, { Fragment } from 'react';
import { string, func } from 'prop-types';
import gql from 'graphql-tag';
import { ApolloConsumer, Query, Mutation } from 'react-apollo';
import ErrorBoundary from 'components/ErrorBoundary';

const RATING_QUERY = gql`
  query rating($recipeId: Int!) {
    rating(input: { ratedType: "Recipe", ratedId: $recipeId }) {
      totalRatings
      average
    }
  }
`;

const USER_RATING_QUERY = gql`
  query getUserRating($recipeId: Int!) {
    getUserRating(input: { ratedType: "Recipe", ratedId: $recipeId }) {
      rating
      createdAt
      updatedAt
    }
  }
`;

export const MUTATION = gql`
  mutation rate($recipeId: Int!, $userRating: Int!) {
    rate(
      input: { ratedType: "Recipe", ratedId: $recipeId, rating: $userRating }
    ) {
      rating {
        ratedId
        ratedType
        rating
        updatedAt
        createdAt
      }
    }
  }
`;

/**
 * makes a GQL query to get the recipe ratings data for the provided recipeId
 * and then provides all children with ratingsData* props and the rateRecipe
 * promise.
 *
 */
function RecipeRatingsDataProvider(props) {
  const { recipeId, children } = props;

  // Save errors on the client and catch on the server
  const errorPolicy = process.browser ? 'all' : 'none';

  // Recipe IDs are stored as a string, but the ratings query uses an int
  const recipeIdInt = parseInt(recipeId, 10);

  return (
    <ErrorBoundary>
      <Mutation mutation={MUTATION}>
        {(mutate, { loading: mutateLoading }) => (
          <Query
            query={RATING_QUERY}
            variables={{ recipeId: recipeIdInt }}
            errorPolicy={errorPolicy}
          >
            {({
              data: queryData,
              loading: queryLoading,
              error: queryError
            }) => {
              // if query fails for any reason, just return children without
              // any props
              if (queryError || queryLoading) {
                return <Fragment>{children({})}</Fragment>;
              }

              // CLEANUP: do something with mutateError?

              // mutate returns a promise so you can do rateRecipe().then()
              const rateRecipe = userRating =>
                mutate({
                  variables: {
                    recipeId: recipeIdInt,
                    userRating
                  }
                  // CLEANUP: figure out how to use optimisticResponse correctly
                  //
                  // optimisticResponse: {
                  //   __typename: 'Mutation',
                  //   rate: {
                  //     __typename: 'CreateRatingPayload',
                  //     rating: {
                  //       __typename: 'Rating',
                  //       rating: userRating
                  //     }
                  //   }
                  // }
                });

              // Note: the query returns `ratings: null` if a recipe does not have
              // any ratings, instead of `ratings: { totalRatings: 0, etc }`. Set
              // a default value for the response so that the rating component will
              // render.
              // CLEANUP: preferably the rating component would handle defaults
              // NOTE: attaching recipeId so it can be read from the rating component
              const ratingsData = queryData.rating
                ? queryData.rating
                : { totalRatings: 0, average: 0 };

              return (
                <ApolloConsumer>
                  {client => (
                    <Fragment>
                      {children({
                        ratingsData,
                        rateRecipe,
                        rateRecipeIsLoading: mutateLoading,
                        fetchUserRating: () =>
                          client.query({
                            query: USER_RATING_QUERY,
                            variables: { recipeId: recipeIdInt }
                          })
                      })}
                    </Fragment>
                  )}
                </ApolloConsumer>
              );
            }}
          </Query>
        )}
      </Mutation>
    </ErrorBoundary>
  );
}

RecipeRatingsDataProvider.propTypes = {
  recipeId: string.isRequired,
  children: func.isRequired
};

export default RecipeRatingsDataProvider;
